import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
// Iter XX.267c — hidden dev/QA-only haptic diagnostic. Attaches
// `window.__gullyHapticDiag()` on native builds so QA can prove the
// plugin bridge is alive from Safari's Web Inspector without any UI.
import { hapticDiagnostic } from "./lib/haptics";
import { isNative } from "./lib/nativePlatform";
if (typeof window !== "undefined" && isNative && isNative()) {
  try { window.__gullyHapticDiag = hapticDiagnostic; }
  catch { /* diagnostic must never break app boot */ }
}
import LandingSwitch from "./pages/LandingSwitch";
import InstantChatLanding from "./pages/InstantChatLanding";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import GoogleOAuthCallback from "./pages/GoogleOAuthCallback";
import PublicReport from "./pages/PublicReport";
import Upgrade from "./pages/Upgrade";
import PlusSuccess from "./pages/PlusSuccess";
import MyCricket from "./pages/MyCricket";
import CricketPage from "./pages/CricketPage";
import PulsePage from "./pages/PulsePage";
import ChallengeDetail from "./pages/ChallengeDetail";
// Iter XX.274 — Founding 100 · web-only one-time pre-sell.
import Founding100Landing from "./pages/Founding100Landing";
import Founding100Welcome from "./pages/Founding100Welcome";
import PulseProfilePage from "./pages/PulseProfilePage";
import Admin from "./pages/Admin";
import FounderCommandCenter from "./pages/FounderCommandCenter";
import FounderStoryMoments from "./pages/FounderStoryMoments";
import LegacyAdmin from "./pages/LegacyAdmin";
import FounderIntel from "./pages/FounderIntel";
import AdminOnboardingReview from "./pages/AdminOnboardingReview";
import AdminInsights from "./pages/AdminInsights";
import AdminDecisionTrace from "./pages/AdminDecisionTrace";
import AdminMiniAhaTrace from "./pages/AdminMiniAhaTrace";
import AccountDelete from "./pages/AccountDelete";
import DeleteAccountRequest from "./pages/DeleteAccountRequest";
import AdminActivationFunnel from "./pages/AdminActivationFunnel";
import ClarityProvider from "./components/ClarityProvider";
import AndroidDownloadBanner from "./components/AndroidDownloadBanner";
import Chat from "./pages/Chat";
import AuthedHomeHeroPreview from "./pages/AuthedHomeHeroPreview";
import MobileNavPreview from "./pages/MobileNavPreview";
import Journey from "./pages/Journey";
import PracticeDebug from "./pages/PracticeDebug";
import Me from "./pages/Me";
import Privacy from "./pages/Privacy";
import LessonCaptureNew from "./pages/LessonCaptureNew";
import SecondInningsCricket from "./pages/SecondInningsCricket";
import SecondInningsBook from "./pages/SecondInningsBook";
import SecondInningsSession1 from "./pages/SecondInningsSession1";
import SecondInningsSession1Success from "./pages/SecondInningsSession1Success";
import Terms from "./pages/Terms";
import Trust from "./pages/Trust";
import LegalPage from "./pages/LegalPage";
import Timeline from "./pages/Timeline";
import PassRedemption from "./pages/PassRedemption";
import { JoinTeamPage, CoachSignInPage, CoachDashboardPage } from "./pages/Teams";
import PartnerProfile from "./pages/PartnerProfile";
import PartnerClaim from "./pages/PartnerClaim";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerEdit from "./pages/PartnerEdit";
import MarketingStudio from "./pages/MarketingStudio";
import PartnerWallet from "./pages/PartnerWallet";
import EarnHub from "./pages/EarnHub";
import TeamPulse from "./pages/TeamPulse";
import ReferralRedirect from "./pages/ReferralRedirect";

// Iter XX.72.6 — <Navigate> from react-router v6 does NOT preserve the
// current URL search string when redirecting to an absolute `to`. That
// silently broke the anonymous → identity → checkout autostart flow:
// `/upgrade?from=paywall&autostart=1` was being rewritten to `/plus` with
// no query, so Upgrade.jsx never saw `autostart=1` and never re-fired
// checkout. This tiny wrapper carries `useLocation().search` along.
function NavigateWithSearch({ to, replace = true }) {
  const location = useLocation();
  const suffix = location.search || "";
  return <Navigate to={`${to}${suffix}`} replace={replace} />;
}

// Iter 54 — Protected wrapper. Forwards the current path as
// `?return_to=` so logged-out users who land on `/plus`, `/admin`,
// etc. are returned there after auth instead of being dumped on
// `/chat` (or the legacy `/dashboard`).
function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">…</div>;
  if (!user) {
    const returnTo = `${location.pathname}${location.search || ""}`;
    const qs = returnTo && returnTo !== "/login"
      ? `?return_to=${encodeURIComponent(returnTo)}`
      : "";
    return <Navigate to={`/login${qs}`} replace />;
  }
  return children;
}

function Router() {
  const location = useLocation();
  // CRITICAL: handle OAuth session_id BEFORE normal routes
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // P0 2026-10-01 — Gully-owned Google OAuth callback (no Emergent).
  // Google redirects to the pre-registered
  // https://askgully.com/api/auth/google/callback?code=...&state=...
  // which serves the SPA shell; handle it BEFORE normal routes.
  if (location.pathname === "/api/auth/google/callback")
    return <GoogleOAuthCallback />;

  // Iter XX.216 — Android download CTA suppression list.
  // Iter XX.217 — `/chat` REMOVED from suppression: the banner is
  // now shown there in a compact top-slim variant that never overlaps
  // the fixed composer (see AndroidDownloadBanner variant switch).
  // Suppressed only on admin / auth / account / partner surfaces
  // where a promotional CTA is inappropriate.
  // Iter XX.274 — also suppressed on /founding100 + /founding100/welcome
  // so the Founding 100 page has ONE primary conversion action only.
  // Iter XX.281 — also suppressed on /second-innings-cricket so the
  // Second Innings sub-brand isn't visually overtaken by the Gully CTA.
  const _path = location.pathname || "/";
  const _suppressAndroidCta =
    _path.startsWith("/admin")
    || _path.startsWith("/auth")
    || _path.startsWith("/me")
    || _path.startsWith("/partner")
    || _path.startsWith("/founding100")
    || _path.startsWith("/second-innings-cricket")
    || _path.startsWith("/second-innings/session-1")
    || _path.startsWith("/second-innings/book");

  return (
    <>
      {/* Iter XX.79 — Clarity loader + route-aware recording gate.
          Renders nothing. Stops recording on sensitive routes
          (payments / auth / account). */}
      <ClarityProvider />
      {/* Iter XX.216 — Android download CTA. Sticky bottom banner on
          web; self-guarded on native / iOS Safari / Plus users /
          dismissed sessions. Suppressed on high-density surfaces
          (see `_suppressAndroidCta` above) to preserve chat / admin
          UX. */}
      {!_suppressAndroidCta && <AndroidDownloadBanner />}
      <Routes>
      <Route path="/" element={<LandingSwitch />} />
      {/* Iter XX.115 — Acquisition-aware bucket landings. Same
          component, config-driven per slug. Each Meta ad points
          at ONE of these. */}
      <Route path="/performance"      element={<InstantChatLanding landingPageId="performance" />} />
      <Route path="/confidence"       element={<InstantChatLanding landingPageId="confidence" />} />
      <Route path="/batting"          element={<InstantChatLanding landingPageId="batting" />} />
      <Route path="/bowling"          element={<InstantChatLanding landingPageId="bowling" />} />
      <Route path="/parents"          element={<InstantChatLanding landingPageId="parents" />} />
      <Route path="/selection"        element={<InstantChatLanding landingPageId="selection" />} />
      <Route path="/identity"         element={<InstantChatLanding landingPageId="identity" />} />
      <Route path="/improvement"      element={<InstantChatLanding landingPageId="improvement" />} />
      <Route path="/situations"       element={<InstantChatLanding landingPageId="situations" />} />
      <Route path="/junior-parents"   element={<InstantChatLanding landingPageId="junior-parents" />} />
      <Route path="/chat" element={<Chat />} />
      {/* Iter XX.265 — Design preview for AuthedHomeHero (dev/QA only,
          not linked from any user surface). */}
      <Route path="/preview/home-hero" element={<AuthedHomeHeroPreview />} />
      <Route path="/preview/mobile-nav" element={<MobileNavPreview />} />
      {/* Iter XX.154.1 — Lesson Capture (conversational). Isolated
          from coaching arc, IDE, paywall, journey FSM. */}
      <Route path="/lessons/new" element={<LessonCaptureNew />} />
      <Route path="/second-innings-cricket" element={<SecondInningsCricket />} />
      {/* Iter XX.291 — canonical multi-session booking page. */}
      <Route path="/second-innings/book" element={<SecondInningsBook />} />
      {/* Iter XX.290 — legacy `/session-1` URL, kept as a redirect
          alias so pre-XX.291 personalized links keep working. */}
      <Route path="/second-innings/session-1" element={<SecondInningsSession1 />} />
      <Route path="/second-innings/session-1/success" element={<SecondInningsSession1Success />} />
      <Route path="/me" element={<Me />} />
      <Route path="/journey" element={<Journey />} />
      <Route path="/practice/debug" element={<PracticeDebug />} />
      <Route path="/coach-first" element={<Navigate to="/" replace />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/trust" element={<Trust />} />
      <Route path="/legal/:policyType" element={<LegalPage />} />
      {/* Public sharing — kept for inbound shared links */}
      <Route path="/share/:id" element={<PublicReport />} />
      <Route path="/report/:id" element={<PublicReport />} />
      <Route path="/login" element={<Login />} />

      {/* Iter XX.190 — Account deletion (Play Store data-deletion req.).
          Public route by design: unauthenticated visitors (Play
          reviewers, etc.) can reach it and read instructions.
          Signed-in state is required to actually delete. */}
      <Route path="/account/delete" element={<AccountDelete />} />
      {/* Iter XX.196 — Google Play compliant public account-deletion
          request page. No auth required. Explains what gets deleted,
          two request paths (in-app self-serve or email), retention
          posture, and 7-day manual-processing commitment. */}
      <Route path="/delete-account" element={<DeleteAccountRequest />} />

      {/* Gully Plus */}
      <Route path="/plus" element={<Upgrade />} />
      <Route path="/my-cricket" element={<MyCricket />} />
      {/* Iter XX.242 — Cricket Graph V1 (free-tier). */}
      <Route path="/cricket" element={<CricketPage />} />
      {/* Iter XX.243 — Gully Pulse (community MVP). */}
      <Route path="/pulse" element={<PulsePage />} />
      <Route path="/pulse/u/:userId" element={<PulseProfilePage />} />
      {/* Iter XX.272 — Gully Challenge #001. Rendered surface is
          double-gated inside the page (feature flag + challenge doc
          enabled). Route is always mounted; disabled challenges
          render an "unavailable" message. */}
      <Route path="/challenge/:id" element={<ChallengeDetail />} />
      {/* Iter XX.274 — Gully Pro Founding 100 · web-only pre-sell.
          Public / anonymous-safe. Backend enforces both gates
          (feature flag + paid count < 100). Both routes render
          "hidden" / "full" states without ever exposing the
          checkout when either gate blocks. */}
      <Route path="/founding100" element={<Founding100Landing />} />
      <Route path="/founding100/welcome" element={<Founding100Welcome />} />
      <Route path="/plus/success" element={<PlusSuccess />} />

      {/* Iter XX.16 — Gully Pass redemption (public route; auth handled inline). */}
      <Route path="/pass/:token" element={<PassRedemption />} />

      {/* Iter XX.41 — Gully for Teams (public join + coach dashboard) */}
      <Route path="/join/:code" element={<JoinTeamPage />} />
      <Route path="/coach/signin" element={<CoachSignInPage />} />
      <Route path="/coach" element={<Protected><CoachDashboardPage /></Protected>} />

      {/* Iter XX.43 — Gully Partner Foundation.
          Canonical `/@:username` handled below by matching a leading `@`
          via a wildcard splat, since React Router 7 doesn't reliably
          match literal `@` in path patterns.
          Fallback: /p/:username. Referral: /r/:code. */}
      <Route path="/p/:username" element={<PartnerProfile />} />
      <Route path="/r/:code" element={<ReferralRedirect />} />
      <Route path="/partner/claim" element={<Protected><PartnerClaim /></Protected>} />
      <Route path="/partner/edit" element={<Protected><PartnerEdit /></Protected>} />
      <Route path="/partner/studio" element={<Protected><MarketingStudio /></Protected>} />
      <Route path="/partner/wallet" element={<Protected><PartnerWallet /></Protected>} />
      <Route path="/partner/team/:team_id/pulse" element={<Protected><TeamPulse /></Protected>} />
      <Route path="/partner" element={<Protected><PartnerDashboard /></Protected>} />
      {/* Iter XX.46 — /earn is the outward-facing name. Non-partners see
          the EarnHub landing; partners are routed to their dashboard
          via the same guard so nothing is hidden. */}
      <Route path="/earn" element={<Protected><PartnerDashboard /></Protected>} />
      {/* Catch-all handle route — declared LAST (before `*`) so specific
          literal routes above win by RR's specificity scoring. The
          <PartnerProfile> component itself validates that the handle
          starts with `@` and 404s otherwise. */}
      <Route path="/:handle" element={<PartnerProfile />} />
      {/* Iter 55 — Cricket Timeline (Plus-gated). Server returns 402
          for non-Plus users; the page handles that by redirecting to
          /plus?reason=timeline. We wrap in Protected so logged-out
          users get a return_to=/timeline first. */}
      <Route path="/timeline" element={<Protected><Timeline /></Protected>} />

      {/* Iter 54 — Legacy pre-WhatsApp routes redirect to current product
          surface so users never see old onboarding/dashboard UI again. */}
      <Route path="/upgrade" element={<NavigateWithSearch to="/plus" />} />
      <Route path="/onboarding" element={<Navigate to="/chat" replace />} />
      <Route path="/dashboard" element={<Navigate to="/chat" replace />} />
      <Route path="/reflect" element={<Navigate to="/chat" replace />} />
      <Route path="/reflect/:id" element={<Navigate to="/chat" replace />} />
      <Route path="/history" element={<Navigate to="/chat" replace />} />
      <Route path="/progress" element={<Navigate to="/chat" replace />} />
      <Route path="/memory" element={<Navigate to="/chat" replace />} />
      <Route path="/report-card" element={<Navigate to="/chat" replace />} />
      <Route path="/ambassadors" element={<Navigate to="/chat" replace />} />

      {/* Admin (still requires auth) */}
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/command" element={<Protected><FounderCommandCenter /></Protected>} />
      <Route path="/admin/story-moments" element={<Protected><FounderStoryMoments /></Protected>} />
      <Route path="/admin/legacy" element={<Protected><LegacyAdmin /></Protected>} />
      <Route path="/admin/founder-intel" element={<Protected><FounderIntel /></Protected>} />
      <Route path="/admin/onboarding-review" element={<Protected><AdminOnboardingReview /></Protected>} />
      <Route path="/admin/insights" element={<Protected><AdminInsights /></Protected>} />
      <Route path="/admin/decision-trace" element={<Protected><AdminDecisionTrace /></Protected>} />
      <Route path="/admin/mini-aha-trace" element={<Protected><AdminMiniAhaTrace /></Protected>} />
      <Route path="/admin/activation-funnel" element={<Protected><AdminActivationFunnel /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/**
 * XX.285j — Native OAuth deep-link listener.
 * Listens for gully:appurl events carrying com.gullyapp.mobile://oauth/callback.
 * Redeems the ticket, stores the JWT, and navigates to /chat.
 * Failures return the user to the login screen — never stranded.
 */
function NativeOAuthListener() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const handler = async (event) => {
      const url = event?.detail?.url || "";
      if (!url.startsWith("com.gullyapp.mobile://oauth/callback")) return;
      try {
        const { handleOAuthDeepLink } = await import("./lib/nativeGoogleAuth");
        const { user } = await handleOAuthDeepLink(url, { setUser });
        if (!cancelled) {
          // Authenticated — return to the Gully experience.
          // REMEMBERED/mission will reflect the claimed state on next load.
          navigate("/chat", { replace: true });
        }
      } catch (e) {
        // Failed/abandoned — return to login, never stranded.
        if (!cancelled) {
          navigate("/login?error=oauth_failed", { replace: true });
        }
      }
    };
    window.addEventListener("gully:appurl", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("gully:appurl", handler);
    };
  }, [setUser, navigate]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NativeOAuthListener />
        <Router />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
